import { BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_arrow_short_right } from '@repo/icons/index'
import YouTube from 'react-youtube'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_silcon, n_slicon, s_invite } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import {
  LOGOS,
  PARTICIPATION_STEP_1,
  PARTICIPATION_STEP_2,
  PARTICIPATION_STEP_3,
  POINT_RED,
  TICKET,
  pastParticipantsImages,
  siliconValleyChallengeHero,
} from '../../../../modules/images'
import ChallengeApplyModal from './ChallengeApplyModal'
import MyChallengeStatus from './MyChallengeStatus'
import useChallenge from './useChallenge'

const challengeParticipationSteps = [
  {
    step: 1,
    describe: n_silcon.how_to_section_1,
    image: PARTICIPATION_STEP_1,
  },
  {
    step: 2,
    describe: n_silcon.how_to_section_2,
    image: PARTICIPATION_STEP_2,
  },
  {
    step: 3,
    describe: n_silcon.how_to_section_3,
    image: PARTICIPATION_STEP_3,
  },
] as const

const SiliconValleyChallenge = () => {
  const { t } = useTranslation()

  const {
    isChallengeDataFetching,
    selectedDuration,
    challengeInfo,
    challengeProgress,
    isApplyModalOpen,
    setSelectedDuration,
    setIsApplyModalOpen,
    handleChallengeApply,
    handleChallengeApplyConfirm,
    isRingleMember,
    handleRemoveChallenge,
  } = useChallenge()

  const handleBlogViewMore = () => {
    const blogPage = 'https://www.ringleplus.com/ko/student/landing/blog/tags/%EC%B1%8C%EB%A6%B0%EC%A7%80'
    window.open(blogPage, '_blank')
  }

  const isMobile = useIsMobile()

  const mainImage = isMobile ? siliconValleyChallengeHero.mainImage_mo : siliconValleyChallengeHero.mainImage

  const youtubeSize = {
    width: isMobile ? '335' : '830',
    height: isMobile ? '188' : '466',
  }

  if (isChallengeDataFetching) return null

  return (
    <>
      {isRingleMember && <button onClick={handleRemoveChallenge}>참가 신청 취소하기 (Ringle member only)</button>}
      <section
        style={{ background: 'linear-gradient(114deg, #84CEFD 19.31%, #C8EAFF 80.69%)' }}
        className="relative sm:px-180 sm:pt-120 sm:pb-[160px] py-[60px] w-full flex items-center justify-center gap-x-[102px]  overflow-x-hidden"
      >
        {/* <div className="absolute inset-0 z-0">
          <Image src={backgroundImage} alt="Background" fill sizes="100vw" priority className="object-cover" />
        </div> */}
        <div className="relative z-10 w-full sm:max-w-[1080px] sm:mx-auto px-20 sm:px-0 flex flex-col sm:flex-row items-center">
          <div className="sm:w-1/2 flex">
            <div className="flex flex-col gap-y-44 justify-center">
              <div className="flex flex-col-reverse sm:flex-row gap-y-40 sm:gap-0">
                <div className="flex flex-col items-center sm:items-start">
                  <span className="text-[28px] sm:text-[32px] font-bold">{t(n_silcon.title1_5th)}</span>
                  <span className="text-[#37298A] text-[46px] sm:text-[56px] font-extrabold">
                    {t(n_silcon.title2_5th)}
                  </span>
                </div>

                {isMobile && (
                  <div className="mx-auto">
                    <img src={mainImage} alt="mainImage" width={335} height={316} />
                  </div>
                )}
              </div>

              <div className="flex flex-col-reverse	sm:flex-col gap-y-32">
                <div className="px-20 py-16 flex sm:flex-col items-center sm:items-start justify-center gap-x-10 border-t border-gray-500">
                  <span className="Body1-16Md sm:Title2-18Md text-gray-900">{t(n_silcon.count_number)}</span>
                  <div className="flex gap-x-4 sm:items-baseline items-center">
                    <span className="Hero4-32Bd sm:Hero3-36Bd">
                      {challengeInfo?.registrationCount?.toLocaleString() || 0}
                    </span>
                    <span className="Headline2-24Md sm:text-32 sm:leading-[50px]">{t(s_invite.people)}</span>
                  </div>
                </div>

                {challengeInfo.isRegistered ? (
                  <div className="py-14 w-full bg-red-100 border-[1.5px] border-red-400 rounded-full text-center">
                    <span className="Title1-20Bd text-red-400">
                      {t(challengeInfo.isStarted ? n_slicon.register_done : n_silcon.cta_button_done)}
                    </span>
                  </div>
                ) : (
                  <button
                    className="p-10 pl-28 w-full flex justify-between items-center bg-[#37298A] rounded-full"
                    onClick={handleChallengeApply}
                  >
                    <span className="Title1-20Bd text-[#FFD67D]">
                      {t(challengeInfo.isStarted ? n_silcon.cha_cta_btn : n_silcon.cta_button)}
                    </span>
                    <div className="p-2 w-36 h-36 rounded-full bg-[#FFD67D] flex items-center justify-center">
                      <QIcon_arrow_short_right width={36} height={36} color="#37298A" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {!isMobile && (
            <div className="sm:w-1/2">
              <img src={mainImage} alt="mainImage" width={573} height={549} />
            </div>
          )}
        </div>
      </section>

      <MyChallengeStatus
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        challengeInfo={challengeInfo}
        challengeProgress={challengeProgress}
      />

      <section className="pt-48 pb-[108px] sm:py-96 bg-purple-900 flex flex-col gap-y-[96px] overflow-x-hidden">
        <div className="sm:pb-88 w-full sm:max-w-[1000px] sm:mx-auto flex flex-col sm:flex-row items-center sm:items-start justify-start gap-y-36">
          <div className="sm:w-1/2 flex flex-col items-center sm:items-start">
            <span className="Headline2-24Bd sm:Hero3-36Bd text-white">{t(n_silcon.slogan)}</span>
          </div>
          <div className="sm:w-1/2 flex flex-col">
            <div className="flex flex-col items-center sm:items-start">
              <span className="Body1-16Md sm:Headline2-24Md text-white">{t(n_silcon.slogan_sub)}</span>
            </div>
          </div>
        </div>

        <div className="relative w-full overflow-hidden group flex flex-col gap-y-24 sm:gap-y-12">
          <div className="w-[200%] h-[250px] sm:h-[500px] flex animate-slide group-hover:[animation-play-state:paused]">
            <img src={pastParticipantsImages} alt="Sliding Image" className="w-full h-full object-cover" />
            <img src={pastParticipantsImages} alt="Sliding Image Clone" className="w-full h-full object-cover" />
          </div>
          <div className="sm:max-w-[1000px] w-full sm:mx-auto flex justify-center sm:justify-start">
            <span className="Body2-14Md sm:Body1-16Md text-[#C9CCDE]">
              {t([
                '2기 실리콘밸리 챌린지, 미국 캘리포니아에서, 2022.8',
                '2nd Silicon Valley Challenge, California, USA, August 2022',
              ])}
            </span>
          </div>
          <div
            className="hidden sm:block absolute top-0 left-0 w-32 h-full pointer-events-none"
            style={{ background: 'linear-gradient(90deg, #120B60 0%, rgba(18, 11, 96, 0) 100%)' }}
          />
          <div
            className="hidden sm:block absolute top-0 right-0 w-32 h-full pointer-events-none"
            style={{ background: 'linear-gradient(-90deg, #120B60 0%, rgba(18, 11, 96, 0) 100%)' }}
          />
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto sm:pb-80 sm:pt-64 py-72 px-20 sm:px-0 bg-white flex flex-col gap-y-36">
        <div className="flex flex-col items-center gap-y-2">
          <span className="Hero4-32Bd sm:Hero3-36Bd text-gray-400">{t(n_silcon.how_to_title_1)}</span>
          <span className="Hero4-32Bd sm:Hero3-36Bd text-black">{t(n_silcon.how_to_title_2)}</span>
        </div>
        <div className="flex sm:flex-row flex-col gap-20 w-full">
          {challengeParticipationSteps.map((step) => (
            <div key={step.step} className="w-full h-[320px] py-32 px-24 flex flex-col gap-y-16 border rounded-2xl">
              <div className="px-10 py-4 self-start bg-gray-200 rounded">
                <span className="Body2-14Bd text-gray-700">STEP {step.step}</span>
              </div>
              <div>
                <div>
                  <img src={step.image} alt={`step${step.step}`} />
                </div>
                <span className="Headline2-24Bd text-black whitespace-pre-line">{t(step.describe)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full sm:pt-96 sm:pb-140 py-72 px-20 bg-purple-900">
        <div className="max-w-[1000px] mx-auto flex flex-col gap-y-40">
          <div className="flex flex-col items-center">
            <span className="Hero4-32Bd sm:Hero3-36Bd text-gray-400">{t(n_silcon.reward_title_1)}</span>
            <span className="Hero3-36Bd text-white">{t(n_silcon.reward_title_2)}</span>
          </div>
          <div className="w-full flex flex-col gap-y-32 items-center">
            <div className="sm:w-1/2 w-full py-40 px-32 flex flex-col sm:flex-row gap-y-16 rounded-2xl bg-[#4838DC33] border border-[#4C3CB1] justify-between items-center">
              <div className="flex flex-col items-center sm:items-start">
                <span className="Title2-18Bd text-blue-200">{t(n_silcon.reward_1_title)}</span>
                <span className="Headline2-24Bd text-white">{t(n_silcon.reward_1_subtitle)}</span>
              </div>
              <img src={POINT_RED} alt="point" height={80} width={80} />
            </div>

            <div className="p-32 px-20 sm:px-32 sm:w-1/2 gap-y-32 sm:gap-y-0 rounded-2xl bg-[#4838DC33] border border-[#4C3CB1] flex flex-col">
              <div className="py-20 flex flex-col gap-y-16">
                <div className="flex flex-col items-center sm:items-start">
                  <span
                    className="Title2-18Bd"
                    style={{
                      background: 'linear-gradient(106deg, #FC997D 10.57%, #FFBF00 45.39%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t(n_silcon.reward_3_title)}
                  </span>
                  <span className="Headline2-24Bd text-white">{t(n_silcon.reward_3_subtitle)}</span>
                </div>
                <div className="flex justify-center sm:justify-start text-center sm:text-left">
                  <span className="Body1-16Md text-gray-400 whitespace-pre-line">{t(n_silcon.reward_3_content)}</span>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row items-center gap-y-16 sm:justify-end">
                <div>
                  <img src={LOGOS} alt="logos" height={32} width={166} />
                </div>
                <div>
                  <img src={TICKET} alt="ticket" height={70} width={127} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-24 py-72 sm:px-0 sm:pt-96 sm:pb-84 bg-white flex flex-col gap-y-36 items-center">
        <div className="flex flex-col items-center text-center sm:text-left">
          <span className="Headline2-24Bd sm:Hero4-32Bd text-gray-400">{t(n_silcon.review_title_1)}</span>
          <span className="Hero4-32Bd sm:Hero3-36Bd">{t(n_silcon.review_title_2)}</span>
        </div>
        <div className="sm:px-[220px] w-full flex flex-col items-center">
          <YouTube
            videoId={'szHYZtkj5yc'}
            opts={{
              height: youtubeSize.height,
              width: youtubeSize.width,
              playerVars: {
                autoplay: 0,
                controls: 1,
                modestbranding: 1,
              },
            }}
          />
        </div>
        <div className="w-[235px]">
          <DSButton type={BUTTON_TYPE.OUTLINE} onClick={handleBlogViewMore}>
            <DSButton.Label>{t(n_silcon.review_btn_1)}</DSButton.Label>
          </DSButton>
        </div>
      </section>

      {/* <section className="relative w-full h-[681px] bg-gray-50">
        <MyChallengeStatus
          selectedDuration={selectedDuration}
          setSelectedDuration={setSelectedDuration}
          challengeInfo={challengeInfo}
          challengeProgress={challengeProgress}
        />
        <div
          style={{
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.60) 73.72%, #000 100%)',
          }}
          className="absolute top-0 left-0 w-full h-full px-[20%] sm:px-0 flex flex-col items-center justify-center gap-y-16"
        >
          <div className="px-12 py-6 bg-red-500 rounded-lg Title1-20Bd text-white text-center">
            {challengeInfo.startTime && (
              <>
                <CountdownTimer date={challengeInfo.startTime} /> {t(s_credit.remaining)}
              </>
            )}
          </div>
          <span className="Headline1-28Bd sm:Hero3-36Bd text-white whitespace-pre-line text-center">
            {t(n_silcon.waiting_title)}
          </span>
        </div>
      </section> */}

      <section className="pt-72 pb-[174px] sm:py-80 px-20 sm:px-[305px] flex flex-col gap-y-32 bg-white">
        <h3 className="Headline2-24Bd">{t(n_silcon.notice_title)}</h3>
        <ul className="flex flex-col gap-y-24 list-disc list-inside">
          {notice.map((item, index) => (
            <li key={index} className="flex flex-col gap-y-6 li-tag-inside">
              <span className="Body1-16Md text-gray-700">{t(item.title)}</span>
              {item.sub ? (
                <ul className="space-y-12">
                  {item.sub.map((subItem, subIndex) => (
                    <div key={subIndex} className="flex flex-col gap-y-4 pl-5 list-disc list-inside">
                      <span className="Body2-14Md text-gray-700">{t(subItem.title)}</span>
                      {subItem.describe.map((desc, descIdx) => (
                        <li key={descIdx} className="Body2-14Md text-gray-700 li-tag-inside">
                          {t(desc)}
                        </li>
                      ))}
                    </div>
                  ))}
                </ul>
              ) : (
                item.describe.map((desc, idx) => (
                  <span key={idx} className="Body2-14Md text-gray-700 pl-5 list-disc list-item">
                    {t(desc)}
                  </span>
                ))
              )}
            </li>
          ))}
        </ul>
      </section>

      <ChallengeApplyModal
        isOpen={isApplyModalOpen}
        setIsOpen={setIsApplyModalOpen}
        onApply={handleChallengeApplyConfirm}
      />
    </>
  )
}

export default SiliconValleyChallenge

const notice = [
  {
    title: n_silcon.notice_title_1,
    describe: [
      ['모든 일정은 한국 시간 기준입니다.', 'All dates and times are based on Korean standard time (KST).'],
      [
        '혜택 당첨자 공지는 발표일에 개별 안내해 드립니다.',
        'Winners of the Silicon Valley Tour will be notified individually on the scheduled date of announcement.',
      ],
      [
        '잘못된 개인 정보 기재로 당첨자 발표 연락을 받지 못한 경우, 혜택 수령이 불가할 수 있습니다.',
        'Failure to receive notification on winning the Silicon Valley Tour due to input of incorrect personal information may result in loss of eligibility to receive the reward.',
      ],
      [
        '당첨자 발표 후 7일 이내에 당첨자와 연락이 닿지 않는 경우 혜택 당첨이 취소되며 다른 사람에게 이관될 수 있습니다.',
        'Winners of the Silicon Valley Tour who cannot be reached within 7 days from the date of announcement will beforfeited, and the reward may be transferred to someone else.',
      ],
      [
        '제공된 혜택은 양도 및 다른 상품으로 교환이 불가합니다.',
        'Any reward provided in relation to the Silicon Valley Challenge is non-transferable and cannot be redeemed for any other reward, product, or benefit.',
      ],
      [
        '실리콘밸리 투어 혜택은 중복 제공되지 않습니다. (포인트 혜택은 중복 제공 가능)',
        'The Silicon Valley Tour benefit can only be awarded once per person. (Point benefits may be awarded multiple times.)',
      ],
    ],
  },
  {
    title: n_silcon.notice_title_2,
    describe: [
      [
        '챌린지 기간은 25/8/1(금)~25/11/30(일) 23:59까지입니다. (11/30(일) 23:30 시작하는 수업 정상 완료 시 스탬프 제공)',
        'The challenge period runs from August 1 (Friday) to November 30 (Sunday), 2025, until 23:59. (Stamps will be awarded for lessons that start at 23:30 on November 30 (Sunday) and are completed successfully.)',
      ],
      [
        '유료, 무료, 체험 수업권 등 종류에 상관없이 모든 수업에 대해 챌린지 참여가 인정됩니다.',
        'All classes, paid or free, including trial sessions, are eligible for challenge participation.',
      ],
      [
        '튜터의 노쇼/미배정이 발생한 경우에도 스탬프를 제공합니다.',
        'Stamps will be awarded even in cases of tutor no-shows or unassigned sessions.',
      ],
      [
        '40분/20분 수업권을 교차 수강할 경우, 40분/20분 수업 수강 혜택이 각각 적용되며, 스탬프도 각각 누적됩니다.',
        'Stamps will be awarded separately for 40-min lessons and 20-min lessons even if you take 40-min and 20-min lessons in alternations.',
      ],
    ],
  },
  {
    title: n_silcon.notice_title_3,
    sub: [
      {
        title: ['수업 수강 혜택', 'Reward for Lesson Completion'],
        describe: [
          [
            '수업 수강 혜택 포인트는 챌린지 달성 시 자동 적립됩니다. (유효기간: 25년 12월 30일 23:59까지)',
            'Reward points for attending lessons will be automatically awarded upon completing a challenge. (Valid until December 30, 2025, at 23:59)',
          ],
          [
            '챌린지 달성 포인트는 20만원 이상 구매 시 사용 가능합니다.',
            'Points from challenge completion can be used for purchases of KRW 200,000 or more.',
          ],
          [
            '참여 시점과 무관하게 챌린지 기간 내 진행한 수업이 있다면 혜택은 소급 적용됩니다.',
            'Rewards for completing lessons will be retroactively applied to any lessons you take during the challenge period regardless of when you joined the challenge.',
          ],
        ],
      },
      {
        title: ['실리콘밸리 투어 혜택', 'Silicon Valley Tour'],
        describe: [
          [
            '참여 시점과 무관하게 챌린지 기간 동안 40분 수업 24회 또는 20분 수업 36회를 정상 완료하면 실리콘밸리 커리어 투어 신청 대상자에 자동 포함됩니다.',
            'Regardless of when you join, you will automatically become eligible for the Silicon Valley Career Tour if you successfully complete either 24 regular 40-minute lessons or 36 regular 20-minute lessons during the challenge period.',
          ],
          [
            '실리콘밸리 커리어 투어 신청 대상자에게 별도 연락을 통해 신청 양식을 보내드립니다. (투어 신청 동기 및 가능 일정 등)',
            'Those who become eligible for the Silicon Valley Tour will be contacted separately and sent an application form (requiring information on motivation for applying and available dates)',
          ],
          [
            '실리콘밸리 투어 신청자에 대해, 선발 현황에 대해서는 25년 12월 ~ 26년 1월 중 별도 연락드립니다.',
            'Applicants for the Silicon Valley Tour will be contacted individually regarding the selection results between December 2025 and January 2026.',
          ],
          [
            '실리콘밸리 커리어 투어는 26년 상반기 중 진행됩니다.',
            'The Silicon Valley Career Tour will take place in the first half of 2026.',
          ],
        ],
      },
    ],
  },
  {
    title: ['[개인 정보 수집 및 이용 안내]', '[Collection and use of personal information]'],
    describe: [
      [
        '링글은 혜택 당첨자 선정 및 발표 목적으로 개인 정보(성함, 연락처, 배송지)를 수집·이용합니다.',
        'Ringle collects and uses personal information (name, contact information, shipping address) for the purpose of selecting and announcing reward winners.',
      ],
      [
        '챌린지 참여자는 개인 정보 수집 및 이용에 대해 거부할 권리를 가지고 있으며, 이를 거부할 시 혜택 당첨에서 제외됩니다.',
        'Challenge participants have the right to object to the collection and use of their personal information, however, doing so will exclude them from receiving or winning rewards.',
      ],
      [
        '수집된 정보는 혜택 당첨자 선정 및 발표, 상품 제공을 위해서만 이용되며, 목적 달성 시 파기됩니다.',
        'The collected information will be used only for the purpose of selecting and announcing winners and providing rewards, and will be discarded after fulfilling the aforementioned purpose.',
      ],
    ],
  },
]
